/* =============================================================================
 * M.M.LaFleur — hero fit-to-viewport.
 *
 * THE PROBLEM
 * -----------
 * The hero is sized in svh, which measures the VIEWPORT. But the hero does not
 * start at the top of the viewport. Above it sit the announcement bar, the
 * header and the secondary category bar — and, when it is switched on, the
 * promo band section. A hero set to 100svh therefore occupies the whole screen
 * PLUS everything stacked above it, and overflows the bottom of the window by
 * exactly the height of that stack.
 *
 * Because the copy is anchored to the bottom of the HERO rather than the
 * bottom of the screen, the heading, the sub-copy and the button all travel
 * off the bottom edge together. That is the "CTA below the fold" report.
 *
 * WHY THIS IS MEASURED RATHER THAN CALCULATED
 * -------------------------------------------
 * The obvious fix is to subtract a known header height in CSS. There isn't
 * one. mm-core.css declares `--mm-header-h: 112px` as a fixed value, and that
 * number predates the secondary category bar — the real stack is meaningfully
 * taller than it now claims. Any constant written here would be wrong the same
 * way, and would go wrong again the next time a row is added, the logo height
 * is changed in the editor, or the announcement bar is dismissed.
 *
 * The stack also is not always there: mm_header.liquid floats the header over
 * the hero on some templates and not others, depending on the "Transparent
 * over hero" setting. Under a floating header the offset is genuinely zero and
 * subtracting anything would leave a strip of page below the hero.
 *
 * So the offset is read off the live layout instead. The hero's distance from
 * the top of the document is, at scroll position zero, exactly the vertical
 * space consumed above it — whatever produced it, and whether that is a
 * spacer, a band, or nothing at all. Published as --mm-hero-offset, which
 * sections/mm_hero.liquid subtracts from both the height and the min-height.
 *
 * NO FEEDBACK LOOP
 * ----------------
 * Setting the variable changes the hero's HEIGHT, never its TOP — nothing
 * above it moves — so re-measuring after a write always yields the same number
 * and the observer settles on the first pass.
 * ========================================================================== */
(function () {
  "use strict";

  var SEL = "[data-mm-hero-fit]";

  function measure() {
    var heroes = document.querySelectorAll(SEL);
    if (!heroes.length) return;

    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;

    for (var i = 0; i < heroes.length; i++) {
      var el = heroes[i];

      /* getBoundingClientRect is viewport-relative, so adding the current
         scroll converts it to a document offset. That makes the reading valid
         at any scroll position rather than only at the top of the page — which
         matters because a resize can fire while the visitor is part way down. */
      var top = el.getBoundingClientRect().top + scrollY;

      /* Negative would mean the hero begins above the document origin, which
         cannot happen; clamp rather than emit a negative that would ADD height
         if it ever did. */
      if (!(top > 0)) top = 0;

      var next = Math.round(top) + "px";
      /* Only write on change. An unconditional write on every resize frame
         would invalidate style on an element that spans the viewport. */
      if (el.style.getPropertyValue("--mm-hero-offset") !== next) {
        el.style.setProperty("--mm-hero-offset", next);
      }
    }
  }

  /* Coalesce bursts — resize, font swap and image decode can all land in the
     same frame. rAF also guarantees the read happens after layout. */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      measure();
    });
  }

  schedule();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
  }

  /* Webfonts are the big one: Genath and Inter both load async, and the
     announcement bar and nav rows reflow when they swap in. */
  window.addEventListener("load", schedule);
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(schedule).catch(function () {});
  }

  window.addEventListener("resize", schedule);
  window.addEventListener("orientationchange", schedule);

  /* The header stack changes height without the window resizing: dismissing
     the announcement bar removes a whole row, and the category bar hides at
     the mobile breakpoint. Watch the header itself rather than polling. */
  if (typeof ResizeObserver === "function") {
    var ro = new ResizeObserver(schedule);
    var watch = document.querySelectorAll("[data-mm-header], .mm-header-spacer, [data-mm-announce]");
    for (var j = 0; j < watch.length; j++) ro.observe(watch[j]);
  }

  /* Barba swaps the main container without a page load, so anything measured
     against the old DOM is stale. Harmless while transitions are disabled. */
  document.addEventListener("barba:after", schedule);
  document.addEventListener("shopify:section:load", schedule);
  document.addEventListener("shopify:section:unload", schedule);
}());
